import { normalizarRol } from './roles';

// Único adaptador frontend de lectura histórica. Nunca usar como payload de
// escritura: cada escritor construye explícitamente el contrato canónico.
// Retirar aliases cuando se haya verificado/regularizado el Owner existente y
// descartado los fixtures/documentos anteriores. No se consulta producción aquí.
export function leerRolDePerfil(data = {}) {
  return normalizarRol(data.rol ?? data.role) || 'pendiente';
}
export function leerEmpresaPrincipal(data = {}) {
  return data.empresaId ?? data.clienteId ?? null;
}
export function leerUsuario(data, uid) {
  if (!data || !uid || data.creadoManual === true) return null;
  const { role, tipoUsuario, authUid, creadoManual, clienteId, clienteNombre,
    name, displayName, fullName, usuario, ...rest } = data;
  const flags = ['invitaciones', 'entradas', 'reservas', 'escaner', 'reportes'];
  const eventosPermitidos = Object.fromEntries(Object.entries(data.eventosPermitidos && typeof data.eventosPermitidos === 'object' && !Array.isArray(data.eventosPermitidos) ? data.eventosPermitidos : {})
    .map(([id, permisos]) => [id, Object.fromEntries(flags.filter((key) => permisos && Object.prototype.hasOwnProperty.call(permisos, key)).map((key) => [key, permisos[key] === true]))]));
  return { ...rest, id: uid, nombre: String(data.nombre || name || displayName || fullName || usuario || '').trim(), eventosPermitidos,
    rol: leerRolDePerfil(data),
    empresaId: leerEmpresaPrincipal(data),
    empresaNombre: data.empresaNombre ?? clienteNombre ?? '' };
}

export function leerOperacion(data = {}) {
  const { eventId, clienteId, clienteNombre, promotorId, promotorUid, promotorNombre, promotorTipo,
    promotorRol, promotorEmpresaId, promotor, creadoPor, createdBy, creadoRol, creadoEn, actualizadoEn,
    opcionPagoId, pagoOpcionId, monto, montoExtras, montoTotal, evento, fechaServidor,
    nombre_comprador, nombreComprador, cliente, nombre_cliente, nombreCliente, buyerName, compradorNombre,
    nombre_evento, eventName, nombreEvento, precioUnitario, precio, total, price, ...rest } = data;
  return { ...rest, eventoId: data.eventoId ?? eventId,
    empresaId: data.empresaId ?? clienteId, empresaNombre: data.empresaNombre ?? clienteNombre ?? '',
    // Un ID de ficha manual no demuestra un UID Auth. Solo el UID histórico
    // explícito puede convertirse; el nombre/rol del histórico siguen visibles.
    atribuidoAUid: data.atribuidoAUid ?? (promotorUid || null),
    atribuidoANombre: data.atribuidoANombre ?? promotorNombre ?? promotor ?? '',
    atribuidoARol: normalizarRol(data.atribuidoARol ?? promotorTipo ?? promotorRol),
    // creadoPor era ambiguo: no inventar un UID desde un nombre/email histórico.
    creadoPorUid: data.creadoPorUid ?? null,
    creadoPorNombre: data.creadoPorNombre ?? creadoPor ?? createdBy ?? '',
    creadoPorRol: normalizarRol(data.creadoPorRol ?? creadoRol),
    createdAt: data.createdAt ?? creadoEn ?? fechaServidor, updatedAt: data.updatedAt ?? actualizadoEn,
    paqueteId: data.paqueteId ?? opcionPagoId ?? pagoOpcionId ?? null,
    eventoNombre: data.eventoNombre ?? evento ?? nombre_evento ?? eventName ?? nombreEvento ?? '',
    ...(data.nombreCliente !== undefined ? { nombreCliente: data.nombreCliente } : {}),
    ...(data.comprador !== undefined || nombre_comprador !== undefined || nombreComprador !== undefined ? { comprador: data.comprador ?? nombre_comprador ?? nombreComprador ?? cliente ?? nombre_cliente ?? buyerName ?? compradorNombre ?? '' } : {}),
    ...(data.montoCentavos !== undefined || monto !== undefined ? { montoCentavos: data.montoCentavos ?? Math.round(monto * 100) } : {}),
    ...(data.montoExtrasCentavos !== undefined || montoExtras !== undefined ? { montoExtrasCentavos: data.montoExtrasCentavos ?? Math.round(montoExtras * 100) } : {}),
    ...(data.montoTotalCentavos !== undefined || montoTotal !== undefined ? { montoTotalCentavos: data.montoTotalCentavos ?? Math.round(montoTotal * 100) } : {}),
  };
}

export function leerEstadoQr(value) {
  const estado = String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
  if (['no_usado', 'no_usada', 'emitida', 'valida', 'válida', 'pendiente'].includes(estado)) return 'no_usada';
  if (['usado', 'usada'].includes(estado)) return 'usada';
  if (['anulado', 'anulada', 'cancelado', 'cancelada'].includes(estado)) return 'anulada';
  if (['bloqueado', 'bloqueada'].includes(estado)) return 'bloqueada';
  return estado;
}

export function leerEvento(data = {}, id = data.id) {
  const { arte, arteEntradas, arteInvitaciones, layout, listaPaquetesId, reservasPaquetesIds, creadoEn, actualizadoEn,
    fecha, horaInicio, horaFin, inicio, iniciaEn, fin, terminaEn, tieneInvitaciones, tieneEntradas, tipoEvento,
    clienteId, clienteNombre, empresa, titulo, ...rest } = data;
  const soloEntrada = tipoEvento === 'solo_entradas' || (tieneEntradas === true && tieneInvitaciones !== true);
  const fechaInicio = data.fechaInicio ?? inicio ?? iniciaEn ?? (fecha ? `${String(fecha).slice(0, 10)}T${horaInicio || '21:00'}` : null);
  const fechaFin = data.fechaFin ?? fin ?? terminaEn ?? null;
  const fechaTexto = typeof fechaInicio === 'string' ? fechaInicio.slice(0, 10) : fechaInicio?.toDate?.().toLocaleDateString('es-BO') || '';
  return { ...rest, ...(id ? { id } : {}), nombre: data.nombre ?? titulo ?? '', fechaInicio, fechaFin, fechaTexto,
    empresaId: data.empresaId ?? clienteId, empresaNombre: data.empresaNombre ?? clienteNombre ?? empresa ?? '',
    arteInvitacion: data.arteInvitacion ?? arteInvitaciones ?? (soloEntrada ? '' : arte) ?? '',
    arteEntrada: data.arteEntrada ?? arteEntradas ?? (soloEntrada ? arte : '') ?? '', layoutInvitacion: data.layoutInvitacion ?? layout,
    listaReservaId: data.listaReservaId ?? listaPaquetesId ?? '', createdAt: data.createdAt ?? creadoEn, updatedAt: data.updatedAt ?? actualizadoEn };
}

export function leerProducto(data = {}) {
  const { entradas, cantEntradas, cantidad, precio, creadoEn, actualizadoEn, ...rest } = data;
  return { ...rest, cantidadEntradas: data.cantidadEntradas ?? entradas ?? cantEntradas ?? cantidad,
    precioCentavos: data.precioCentavos ?? (typeof precio === 'number' ? Math.round(precio * 100) : undefined),
    createdAt: data.createdAt ?? creadoEn, updatedAt: data.updatedAt ?? actualizadoEn };
}

export function leerDocumento(coleccion, data, id) {
  if (!data) return data;
  if (coleccion === 'usuarios') return leerUsuario(data, id);
  if (coleccion === 'empresas') {
    const { name, slug, ...rest } = data;
    return { ...rest, nombre: data.nombre ?? name ?? '' };
  }
  if (coleccion === 'eventos') {
    const evento = leerEvento(data, id);
    return { ...evento, combos: (evento.combos || []).map(leerProducto) };
  }
  if (coleccion === 'paquetesReserva') return leerProducto(data);
  if (['reservas', 'entradas', 'invitaciones', 'logs_reservas'].includes(coleccion)) {
    const operacion = leerOperacion(data);
    if (coleccion === 'entradas') return { ...operacion, estado: leerEstadoQr(data.estado),
      comprador: data.comprador ?? data.nombre_comprador ?? data.nombreComprador ?? data.nombre ?? data.cliente ?? data.nombre_cliente ?? data.nombreCliente ?? data.buyerName ?? data.compradorNombre ?? '',
      ...(data.precioComboCentavos !== undefined || data.precioCombo !== undefined ? { precioComboCentavos: data.precioComboCentavos ?? Math.round(data.precioCombo * 100) } : {}),
      ...(data.precioUnitario !== undefined || data.precio !== undefined || data.price !== undefined ? { precioUnitarioCentavos: Math.round((data.precioUnitario ?? data.precio ?? data.price) * 100) } : {}) };
    return coleccion === 'invitaciones' ? { ...operacion, estado: leerEstadoQr(data.estado) } : operacion;
  }
  return data;
}
